/**
 * IInteractable - Interface for components that can be interacted with by the player.
 * Interactables register themselves with the player's InteractionComponent when in range.
 */

import type { Actor } from '../../actors/Actor.js';
import type * as THREE from 'three';

/**
 * Interface for components that can be interacted with by the player.
 * Implement this interface on SceneComponents that should respond to player interaction input.
 */
export interface IInteractable {
  /**
   * Called when the player begins interaction (interaction key pressed).
   * @param player - The player actor initiating the interaction
   * @returns True if the interaction was handled, false otherwise
   */
  beginInteract(player: Actor): boolean;

  /**
   * Called when the player ends interaction (interaction key released).
   * Optional for hold-to-interact mechanics. Not all interactables need to implement this.
   * @param player - The player actor ending the interaction
   */
  endInteract?(player: Actor): void;

  /**
   * Returns true if this interactable can currently be interacted with by the player.
   * @param player - The player actor attempting to interact
   * @returns True if interaction is currently possible
   */
  canInteract(player: Actor): boolean;

  /**
   * Returns a UI prompt text for this interactable.
   * @returns Text to display to the player (e.g., "Press E to Open Door")
   */
  getInteractionPrompt(): string;

  /**
   * Returns the priority of this interactable for selection.
   * Higher priority interactables are preferred when multiple are available.
   * @returns Priority value (higher = more important)
   */
  getInteractionPriority(): number;

  /**
   * Returns the world position of this interactable for distance calculations.
   * @returns World position as a Vector3
   */
  getWorldPosition(): THREE.Vector3;
}

/**
 * Type guard to check if a component implements IInteractable.
 * @param component - The component to check
 * @returns True if the component implements IInteractable
 */
export function isInteractable(component: unknown): component is IInteractable {
  return (
    component !== null &&
    typeof component === 'object' &&
    'beginInteract' in component &&
    typeof (component as IInteractable).beginInteract === 'function' &&
    'canInteract' in component &&
    typeof (component as IInteractable).canInteract === 'function' &&
    'getInteractionPrompt' in component &&
    typeof (component as IInteractable).getInteractionPrompt === 'function' &&
    'getInteractionPriority' in component &&
    typeof (component as IInteractable).getInteractionPriority === 'function' &&
    'getWorldPosition' in component &&
    typeof (component as IInteractable).getWorldPosition === 'function'
  );
}
